import clsx from "clsx";

import {
  filterButtonClassName,
  filterButtonTextColor,
} from "../SegmentControl/filterButtonStyles";
import { Typo } from "../Typo";

export type FilterButtonOption<T extends string = string> = {
  label: string;
  value: T;
};

export type FilterButtonGroupProps<T extends string = string> = {
  options: FilterButtonOption<T>[];
  values: T[];
  onChange: (values: T[]) => void;
  label?: string;
  layout?: "stacked" | "inline";
  "data-testid"?: string;
};

export const FilterButtonGroup = <T extends string = string>({
  options,
  values,
  onChange,
  label,
  layout = "stacked",
  "data-testid": testId = "filter-button-group",
}: FilterButtonGroupProps<T>) => {
  const isInline = layout === "inline";
  const toggle = (value: T) =>
    onChange(
      values.includes(value)
        ? values.filter((v) => v !== value)
        : options
            .map((o) => o.value)
            .filter((v) => v === value || values.includes(v)),
    );

  return (
    <div
      className={clsx(
        "flex gap-2",
        isInline ? "items-center justify-between" : "flex-col",
      )}
    >
      {label && (
        <Typo element="span" type="labelSM" textColor="text-text-primary">
          {label}
        </Typo>
      )}
      <div
        role="group"
        aria-label={label}
        className={clsx(
          "flex gap-2",
          isInline ? "flex-col items-end" : "flex-wrap",
        )}
        data-testid={testId}
      >
        {options.map((option) => {
          const isSelected = values.includes(option.value);
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={isSelected}
              onClick={() => toggle(option.value)}
              data-testid={`filter_button_group_${option.value}_button`}
              className={clsx(
                "cursor-pointer whitespace-nowrap rounded-lg transition-colors duration-200 ease-out",
                isInline ? "px-3 py-2" : "px-5 py-2.5",
                filterButtonClassName(isSelected),
              )}
            >
              <Typo
                element="span"
                type={isInline ? "labelXS" : "labelSM"}
                textColor={filterButtonTextColor(isSelected)}
              >
                {option.label}
              </Typo>
            </button>
          );
        })}
      </div>
    </div>
  );
};
